import React from 'react';
import PropTypes from 'prop-types';
import { Col } from '@storable/canaveral-components';
import styles from './styles.scss';

const { DNS_VALIDATION_DOMAIN, K8S_INGRESS_CNAME, K8S_INGRESS_REWRITE_IP } = process.env;

const DNSChangesRequiredText = `
  In order to obtain an auto-renewing SSL from Let's Encrypt™, you must first
  update your DNS with the following changes. Once these changes are completed, this
  domain will use the auto-renewing SSL going forward. Records may take 24-48 hours
  to publish in some cases.
`;

const DNSChangesRequiredUploadSuccessText = `
  In order to properly maintain the security of our websites, we will no longer allow
  for external SSL certificates to be uploaded. This domain is eligible for an
  auto-renewing SSL from Let's Encrypt™. Once these changes are completed, this domain will
  use the auto-renewing SSL going forward. Records may take 24-48 hours to publish in some
  cases.
`;

const DNSChangesRequiredCertIsReadyText = `
  Your auto-renewing certificate is ready, however manual DNS changes are needed
  to use it. Please navigate to the DNS hosting provider and update the following records:
`;

const DNSPropagatedText = `
  Once the changes are propagated, this domain will use the automated certificate.
  Until then, the current SSL will remain in place. This may take 5 minutes to 48 hours,
  depending on the time-to-live (TTL) of the records. Please check back soon.
`;

const propTypes = {
  domain: PropTypes.string.isRequired,
  DNSChangesRequired: PropTypes.bool.isRequired,
  SSLUploadSuccess: PropTypes.bool.isRequired,
  certPending: PropTypes.bool.isRequired,
};

const HostnameCertSettingsStatusPanelContent = ({
  domain,
  identifier,
  DNSChangesRequired,
  SSLUploadSuccess,
  certPending,
  certReady,
}) => {
  // cert is ready in kubernetes but dns changes are required
  if (certReady) {
    return (
      <Col className={styles.content}>
        <p>
          {DNSChangesRequiredCertIsReadyText}
        </p>
        <div className={styles.dnsSection}>
          <span><b>Record Name</b>: www.{domain}</span><br />
          <span><b>Record Type</b>: CNAME</span><br />
          <span>
            <b>New Value</b>: {K8S_INGRESS_CNAME}
          </span><br />
        </div>
        <div className={styles.dnsSection}>
          <span><b>Record Name</b>: {domain}</span><br />
          <span><b>Record Type</b>: A</span><br />
          <span>
            <b>New Value</b>: {K8S_INGRESS_REWRITE_IP}
          </span><br />
        </div>
        <p>
          {DNSPropagatedText}
        </p>
      </Col>
    );
  }

  // DNS changes required
  if (DNSChangesRequired) {
    return (
      <Col className={styles.content}>
        <p>
          {SSLUploadSuccess ? DNSChangesRequiredUploadSuccessText : DNSChangesRequiredText}
        </p>
        <div className={styles.dnsSection}>
          <span><b>Record Name</b>: _acme-challenge.www.{domain}</span><br />
          <span>
            <b>New Value</b>: _acme-challenge.www.{identifier}.{DNS_VALIDATION_DOMAIN}
          </span><br />
          <span><b>Type</b>: CNAME</span><br />
        </div>
        <div className={styles.dnsSection}>
          <span><b>Record Name</b>: _acme-challenge.{domain}</span><br />
          <span>
            <b>New Value</b>: _acme-challenge.{identifier}.{DNS_VALIDATION_DOMAIN}
          </span><br />
          <span><b>Type</b>: CNAME</span><br />
        </div>
      </Col>
    );
  }

  // Cert pending
  if (certPending) {
    return (
      <Col className={styles.content}>
        <p>
          Your SSL certificate is being generated. Please check back or click refresh.
        </p>
      </Col>
    );
  }

  // Default, request SSL cert flow
  return (
    <Col className={styles.content}>
      <p>
        In order to properly maintain the security of our websites, we will no longer allow
        for external SSL certificates to be uploaded. This domain is eligible for an
        auto-renewing SSL from Let&apos;s Encrypt™. Click the Request Certificate button to
        request your SSL certificate.
      </p>
    </Col>
  );
};

HostnameCertSettingsStatusPanelContent.propTypes = propTypes;

export default HostnameCertSettingsStatusPanelContent;
